import React from 'react';
import './GameProgressBar.css';
import XhelosTooltip from '@components/ui/general/xhelos-tooltip/XhelosTooltip';
import { useBreakpoint } from '@utils/responsive';

/**
 * GameProgressBar - Mini timeline showing current game phase
 * Displays 4 phases: Deploy, Plan, Resolved, Finish
 *
 * @param {object} props
 * @param {string|object} props.status - Current game status
 */
const GameProgressBar = ({ status }) => {
  const breakpoint = useBreakpoint();
  const isMobileView = breakpoint === 'portrait' || breakpoint === 'mobile';

  // Clean status string (remove 'game_' prefix if present)
  const cleanStatus = status?.value
    ? status.value.replace('game_', '')
    : status?.replace('game_', '') || 'lobby';

  // Define the 4 phases in order
  const phases = [
    { key: 'deploy', label: 'D', fullName: 'Deploy', statusMatch: ['deploy'] },
    { key: 'plan', label: 'P', fullName: 'Plan', statusMatch: ['plan'] },
    { key: 'resolved', label: 'R', fullName: 'Resolved', statusMatch: ['resolve', 'resolved'] },
    { key: 'finish', label: 'F', fullName: 'Finish', statusMatch: ['finish'] },
  ];

  // Find current phase index
  const currentIndex = phases.findIndex(phase =>
    phase.statusMatch.includes(cleanStatus)
  );

  // Get current phase for mobile label
  const currentPhase = currentIndex >= 0 ? phases[currentIndex] : null;

  // If in lobby, no phase is active yet
  const isLobby = cleanStatus === 'lobby';

  const getPhaseState = (index) => {
    if (isLobby) return 'upcoming';
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'upcoming';
  };

  // Mobile: Show only a label with current phase name
  // Key triggers animation on phase change
  if (isMobileView) {
    return (
      <div className="game-progress-bar">
        <div
          key={cleanStatus}
          className={`game-progress-label ${currentPhase ? 'progress-node-current' : ''}`}
        >
          {currentPhase ? currentPhase.fullName : 'Lobby'}
        </div>
      </div>
    );
  }

  // Tablet+: Show full D-P-R-F bar
  return (
    <div className="game-progress-bar">
      {phases.map((phase, index) => {
        const state = getPhaseState(index);
        const isLast = index === phases.length - 1;
        // Key includes cleanStatus for current node to trigger animation on phase change
        const nodeKey = state === 'current' ? `${phase.key}-${cleanStatus}` : phase.key;

        return (
          <React.Fragment key={nodeKey}>
            <XhelosTooltip
              content={phase.fullName}
              placement="bottom"
              theme="dark"
              trigger="hover"
            >
              <div className={`progress-node progress-node-${state}`}>
                {phase.label}
              </div>
            </XhelosTooltip>
            {!isLast && (
              <div className={`progress-connector progress-connector-${
                state === 'completed' || state === 'current' ? 'filled' : 'empty'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default GameProgressBar;
